import {
  INTL_DAY_MONTH_NAME_SHORT,
  INTL_DAY_ONLY,
  INTL_DMY_CASUAL_FORMAT,
  INTL_DMY_CASUAL_SHORT_FORMAT,
  INTL_SHORT_DAY_NAME_DAY_MONTH,
  INTL_SHORT_TIME_FORMAT_AM_PM,
  ISO_DATE_FORMAT,
} from 'constants/dateFormats'
import GeoContext from 'contexts/geoContext'
import { getCurrentIntl } from 'i18n/util/currentIntl'
import { DEFAULT_LOCALE, getLocaleWithWesternDigits } from 'i18n/util/locale'
import moment from 'moment'
import { useContext, useCallback } from 'react'
import { useIntl } from 'react-intl'

/**
 * Returns whether the given date is valid date or not
 * @param date The date to check
 * @returns Whether it is valid
 */
export function isValidDate(date?: Date) {
  return date instanceof Date && !isNaN(date.getTime())
}

export function dateIsAfter(date: Date, date2?: Date) {
  return date2 ? date.getTime() > date2.getTime() : false
}

export function dateIsAfterOrSame(date: Date, date2?: Date) {
  return date2 ? date.getTime() >= date2.getTime() : false
}

export function dateIsBefore(date: Date, date2?: Date) {
  return date2 ? date.getTime() < date2.getTime() : false
}

export function dateIsBeforeOrSame(date: Date, date2?: Date) {
  return date2 ? date.getTime() <= date2.getTime() : false
}

export function startOfMonth(date: Date) {
  return new Date(date.getFullYear(), date.getMonth())
}

export function startOfDay(date: Date) {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate())
}

/**
 * Clamps a date to the nearest month within the given bounds, returning the
 * first day of that month. Comparison is month-granular, so a date inside the
 * same month as a bound is never pushed to the other side of it.
 */
export function clampToMonthRange(date: Date, min?: Date, max?: Date): Date {
  if (!isValidDate(date)) return date

  const month = startOfMonth(date)
  if (min && isValidDate(min) && month < startOfMonth(min)) return startOfMonth(min)
  if (max && isValidDate(max) && month > startOfMonth(max)) return startOfMonth(max)

  return month
}

export function endOfDay(date: Date) {
  return new Date(
    date.getFullYear(),
    date.getMonth(),
    date.getDate(),
    23,
    59,
    59,
    999,
  )
}

export function endOfMonth(date: Date) {
  return new Date(date.getFullYear(), date.getMonth() + 1, 1, 0, 0, 0, -1)
}

export function addMonths(date: Date, toAdd: number) {
  const newDate = new Date(date)
  newDate.setMonth(newDate.getMonth() + toAdd)

  /**
   * If the date jump automatically for the next month in last day of month bug
   * set the date to the last day of previous month to prevent the double month skipping.
   */
  const isNotValidMonth = newDate.getMonth() > (date.getMonth() + toAdd) % 12
  if (toAdd > 0 && isNotValidMonth) newDate.setDate(0)

  return newDate
}

export function isSameDay(date: Date, date2?: Date) {
  return (
    !!date2 &&
    date.getFullYear() === date2.getFullYear() &&
    date.getMonth() === date2.getMonth() &&
    date.getDate() === date2.getDate()
  )
}

export function isSameDayUTC(date: Date, date2?: Date) {
  return (
    !!date2 &&
    date.getUTCFullYear() === date2.getUTCFullYear() &&
    date.getUTCMonth() === date2.getUTCMonth() &&
    date.getUTCDate() === date2.getUTCDate()
  )
}

export function isSameMonth(date: Date, date2?: Date) {
  return (
    !!date2 &&
    date.getFullYear() === date2.getFullYear() &&
    date.getMonth() === date2.getMonth()
  )
}

export function isSameYear(date: Date, date2?: Date) {
  return !!date2 && date.getFullYear() === date2.getFullYear()
}

/**
 * Returns what the naive `date`(/time) would be at `tzString`, but in the system's time zone.
 *
 * For example, if: (i) the current instant is `2026-05-22T02:00:00Z`; (ii) the system time zone is
 * `Australia/Sydney`; and (iii) `tzString` is `America/Vancouver`, then: (i) the local time in
 * Vancouver would be `2026-05-21T19:00:00-07:00`; but (ii) we'd return `2026-05-21T19:00:00+10:00`.
 */
export function convertTZ(date: Date, tzString: string) {
  return new Date(date.toLocaleString('en-US', { timeZone: tzString }))
}

export function getDateFromZonedDateString(
  dateTimeStr: string,
  timeZone: string,
): Date {
  const dateTimeStrInTargetZone = dateTimeStr.includes('T')
    ? dateTimeStr
    : `${dateTimeStr}T00:00:00`
  const localDate = new Date(dateTimeStrInTargetZone)

  const localOffsetMinutes = localDate.getTimezoneOffset()

  const targetOffsetMinutes =
    calculateTimezoneOffset(
      localDate,
      new Date(localDate.toLocaleString('en-US', { timeZone })),
    ) * 60

  const offsetDifference = localOffsetMinutes - targetOffsetMinutes
  const utcDate = new Date(localDate.getTime() + offsetDifference * 60 * 1000)

  return utcDate
}

export function calculateTimezoneOffset(date: Date, date2: Date) {
  const localTimezoneOffset = date.getTimezoneOffset()
  const time = Math.round(date.getTime() / 1000 / 60)
  const time2 = Math.round(date2.getTime() / 1000 / 60)
  const result = time2 - time - localTimezoneOffset
  return Math.round(result / 60)
}

export function subDays(date: Date, days: number): Date {
  return addDays(date, -days)
}

export function addDays(start: Date, days: number): Date {
  const date = new Date(start)
  const originalOffset = date.getTimezoneOffset()
  date.setDate(date.getDate() + days)
  const newOffset = date.getTimezoneOffset()
  if (originalOffset !== newOffset) {
    // Daylight savings has changed.
    // If the offset got less, the clock was wound forward (Spring).
    // If the offset got more, the clock was wound back (Autumn).
    // Either way, we assume callers of this method want to move in whole calendar days,
    // and just undo the offset change.
    const offsetChange = newOffset - originalOffset
    date.setMinutes(date.getMinutes() - offsetChange)
  }
  return date
}

export function addWeeks(start: Date, weeks: number): Date {
  return addDays(start, weeks * 7)
}

export function addYears(start: Date, years: number): Date {
  const date = new Date(start)
  date.setFullYear(date.getFullYear() + years)
  return date
}

export function addHours(start: Date, hours: number): Date {
  const date = new Date(start)
  date.setHours(date.getHours() + hours)
  return date
}

export function beginningOfToday(timezone?: string): Date {
  let date = new Date()
  if (timezone) {
    try {
      date = convertTZ(date, timezone)
    } catch {
      // Tolerate invalid time zones; this is best effort.
    }
  }
  date.setHours(0, 0, 0, 0)
  return date
}

/**
 * Takes a given date and formats it into the ISO *date only* format
 * (no timezone or time data) e.g. 2025-05-26
 * @param date The date to format
 * @returns A string representing ISO date (no time or timezone)
 */
export function formatIsoDate(date: Date): string {
  const timezoneOffset = date.getTimezoneOffset() * 60 * 1000
  return new Date(date.getTime() - timezoneOffset)
    .toISOString()
    .substring(0, 10)
}

export interface DateDifference {
  days: number
  hours: number
  minutes: number
  seconds: number
}
export function getYearDiff(startDate: Date, endDate: Date): number {
  return Math.abs(endDate.getFullYear() - startDate.getFullYear())
}

type RoundingOption = 'day' | 'hour' | 'minute' | 'second'
const millisecondsPer: Record<RoundingOption, number> = {
  day: 1000 * 60 * 60 * 24,
  hour: 1000 * 60 * 60,
  minute: 1000 * 60,
  second: 1000,
}

export function dateDifference(
  differenceUntilDate: Date,
  differenceFromDate = new Date(),
): DateDifference {
  const diff = differenceUntilDate.getTime() - differenceFromDate.getTime()
  if (diff < 0) {
    return {
      days: 0,
      hours: 0,
      minutes: 0,
      seconds: 0,
    }
  }
  const remainingHours = diff % millisecondsPer.day
  const days = Math.floor(diff / millisecondsPer.day)
  const remainingMinutes = remainingHours % millisecondsPer.hour
  const hours = Math.floor(remainingHours / millisecondsPer.hour)
  const remainingSeconds = remainingMinutes % millisecondsPer.minute
  const minutes = Math.floor(remainingMinutes / millisecondsPer.minute)
  const seconds = Math.floor(remainingSeconds / millisecondsPer.second)
  return {
    days,
    hours,
    minutes,
    seconds,
  }
}

export function roundedDateDifference(
  differenceUntilDate: Date,
  differenceFromDate = new Date(),
  precision: RoundingOption,
): number {
  const diff = differenceUntilDate.getTime() - differenceFromDate.getTime()
  return Math.round(diff / millisecondsPer[precision])
}

export function pad(val: number, len = 2): string {
  return ('0'.repeat(len) + String(val)).slice(-len)
}

export function shortTimeHourFormatAmPm(val: Date, utc?: boolean): string {
  const intl = getCurrentIntl()
  return intlDateFormatter(
    val,
    INTL_SHORT_TIME_FORMAT_AM_PM,
    intl.locale,
    utc ? 'UTC' : undefined,
  ).toLocaleLowerCase(intl.locale)
}

type SupportedDateType = Date | moment.Moment | string
type NullableSupportedDateType = SupportedDateType | null | undefined

function toDateObject(value: SupportedDateType): Date {
  if (typeof value === 'string') {
    // This conversion was having troubles with timezones, going back a day in negative timezones
    // When a date string doesn't have a time component, dates assume the time is
    // whatever '00:00:00' would be **in the current timezone**. This then has the timezone
    // applied to it and boom, we're yesterday. The fix appears to be to tell it explicitly
    // what the time is for this date string and we get a date for tha time in the timezone
    const hasTime = value.includes('T')
    return new Date(value + (hasTime ? '' : 'T00:00:00'))
  } else if (moment.isMoment(value)) {
    return value.toDate()
  } else {
    return value
  }
}

const ENGLISH_DATE_FORMATTING_LOCALE_BY_REGION: Partial<
  Record<string, string>
> = {
  AU: 'en-AU',
  CA: 'en-CA',
  GB: 'en-GB',
  HK: 'en-HK',
  IN: 'en-IN',
  NZ: 'en-NZ',
  SG: 'en-SG',
  US: 'en-US',
}

export const selectDateFormattingLocaleForRegion = (
  currentRegionCode: string,
) => {
  return (
    ENGLISH_DATE_FORMATTING_LOCALE_BY_REGION[currentRegionCode] ??
    DEFAULT_LOCALE
  )
}

/**
 * Prefer an explicit non-default request locale; otherwise use the locale baked into
 * locale-specific browser bundles (`BUILD_LOCALE`) so dates match inlined translations.
 */
function resolveActiveAppLocale(
  geoLocale: App.GeoState['locale'],
): App.GeoState['locale'] {
  if (geoLocale && geoLocale !== DEFAULT_LOCALE) {
    return geoLocale
  }

  const buildLocale = process.env.BUILD_LOCALE
  if (buildLocale) {
    return buildLocale
  }

  return geoLocale
}

export function selectDateFormattingLocale(
  locale: App.GeoState['locale'],
  currentRegionCode: string,
) {
  const activeLocale = resolveActiveAppLocale(locale)

  const dateFormattingLocale = activeLocale && !activeLocale.startsWith('en-')
    ? activeLocale
    : selectDateFormattingLocaleForRegion(currentRegionCode)

  return getLocaleWithWesternDigits(dateFormattingLocale)
}

/** Locale for `Intl` date output derived from Redux `geo` (selectors and non-React code paths). */
export function dateFormattingLocaleFromGeo(geo: App.GeoState): string {
  return selectDateFormattingLocale(geo.locale, geo.currentRegionCode)
}

const formatterCache = new Map<string, Intl.DateTimeFormat>()

function getFormatter(
  instruction: Intl.DateTimeFormatOptions,
  locale: string = DEFAULT_LOCALE,
) {
  const key = `${locale}.${JSON.stringify(instruction)}`
  let formatter = formatterCache.get(key)
  if (!formatter) {
    formatter = new Intl.DateTimeFormat(locale, instruction)
    formatterCache.set(key, formatter)
  }
  return formatter
}

export function intlDateFormatter(
  date: NullableSupportedDateType,
  instruction: Intl.DateTimeFormatOptions,
  locale: string = DEFAULT_LOCALE,
  timeZone?: string,
): string {
  if (!date) return ''

  const dateObject = toDateObject(date)
  if (!isValidDate(dateObject)) return ''

  const formatter = getFormatter({ ...instruction, timeZone }, locale)
  return formatter.format(dateObject)
}

const LIB_REFUNDS_LEGACY_UTC_MIDNIGHT_SUBSTRING = 'T00:00:00.000Z'

// TODO: Move date resolution + Intl formatting into `@luxuryescapes/lib-refunds` next to
// `formatDate.formattedShortDateByTimezone`, export a locale-aware helper, bump the package here,
// and replace this function with a thin re-export or direct `formatDate.*` call.

/**
 * Resolves the calendar instant the same way as `formatDate.formattedShortDateByTimezone` in
 * `@luxuryescapes/lib-refunds` (legacy `T00:00:00.000Z` string branch vs `convertTZ` path), then
 * formats with `Intl` so month and weekday names follow `dateFormattingLocale` (e.g. `de-DE`).
 *
 * Does not pass `timeZone` into `Intl` — the resolved `Date` already matches the lib’s wall-time
 * behaviour for display.
 *
 * **Legacy substring branch:** `Intl` uses the host default time zone (same idea as lib-refunds
 * reading local date fields from `new Date(iso)`). If SSR and the browser use different default
 * zones, markup for that branch can disagree between server and client — mirroring lib-refunds
 * until this logic moves upstream (see TODO below).
 */
export function formatHeadlineCancellationPolicyStartForDisplay(
  headlineCancellationPolicyStart: string,
  propertyTimezone: string,
  dateFormattingLocale: string,
): string {
  const timezone = propertyTimezone || 'UTC'
  const isLibRefundsLegacyUtcMidnightString =
    headlineCancellationPolicyStart.includes(
      LIB_REFUNDS_LEGACY_UTC_MIDNIGHT_SUBSTRING,
    )

  const dateForDisplay = isLibRefundsLegacyUtcMidnightString
    ? new Date(headlineCancellationPolicyStart)
    : convertTZ(new Date(headlineCancellationPolicyStart), timezone)

  if (!isValidDate(dateForDisplay)) {
    return ''
  }

  const formatter = getFormatter(
    INTL_SHORT_DAY_NAME_DAY_MONTH,
    dateFormattingLocale,
  )
  return formatter.format(dateForDisplay)
}

export function useIntlDateFormatter() {
  const intl = useIntl()
  const { currentRegionCode, locale: geoLocale } = useContext(GeoContext)
  const locale = selectDateFormattingLocale(
    geoLocale && geoLocale !== DEFAULT_LOCALE ? geoLocale : intl.locale,
    currentRegionCode,
  )
  return useCallback(
    (
      date: NullableSupportedDateType,
      instruction: Intl.DateTimeFormatOptions,
    ) => intlDateFormatter(date, instruction, locale),
    [locale],
  )
}

export function intlDateRangeFormatter(
  startDate: SupportedDateType | undefined,
  endDate: SupportedDateType | undefined,
  instruction: Intl.DateTimeFormatOptions,
  locale: string = DEFAULT_LOCALE,
): string {
  if (!startDate || !endDate) return ''

  const startDateObject = toDateObject(startDate)
  const endDateObject = toDateObject(endDate)
  if (!isValidDate(startDateObject) || !isValidDate(endDateObject)) return ''

  const formatter = getFormatter(instruction, locale)
  // What's the `replace` for? We've intentionally decided not to use &thinsp;, and use &nbsp; instead.
  return formatter
    .formatRange(startDateObject, endDateObject)
    .replace(/\u2009/g, '\u00A0')
}

export function intlHyphenDateRangeFormatter(
  startDate: SupportedDateType | undefined,
  endDate: SupportedDateType | undefined,
  locale: string = DEFAULT_LOCALE,
  includeYearWhenSameYear = true,
): string {
  if (!startDate || !endDate) return ''

  const startDateObject = toDateObject(startDate)
  const endDateObject = toDateObject(endDate)
  if (!isValidDate(startDateObject) || !isValidDate(endDateObject)) return ''

  const sameYear =
    startDateObject.getFullYear() === endDateObject.getFullYear()
  const sameMonth =
    sameYear && startDateObject.getMonth() === endDateObject.getMonth()

  const startInstruction = sameMonth
    ? INTL_DAY_ONLY
    : sameYear
      ? INTL_DAY_MONTH_NAME_SHORT
      : INTL_DMY_CASUAL_SHORT_FORMAT

  const endInstruction =
    sameYear && !includeYearWhenSameYear
      ? INTL_DAY_MONTH_NAME_SHORT
      : INTL_DMY_CASUAL_SHORT_FORMAT

  return `${intlDateFormatter(
    startDateObject,
    startInstruction,
    locale,
  )} - ${intlDateFormatter(endDateObject, endInstruction, locale)}`
}

export function useIntlDateRangeFormatter() {
  const intl = useIntl()
  const { currentRegionCode, locale: geoLocale } = useContext(GeoContext)
  const locale = selectDateFormattingLocale(
    geoLocale && geoLocale !== DEFAULT_LOCALE ? geoLocale : intl.locale,
    currentRegionCode,
  )
  return useCallback(
    (
      startDate: SupportedDateType,
      endDate: SupportedDateType,
      instruction: Intl.DateTimeFormatOptions,
    ) => intlDateRangeFormatter(startDate, endDate, instruction, locale),
    [locale],
  )
}

export function useIntlHyphenDateRangeFormatter() {
  const intl = useIntl()
  const { currentRegionCode, locale: geoLocale } = useContext(GeoContext)
  const locale = selectDateFormattingLocale(
    geoLocale && geoLocale !== DEFAULT_LOCALE ? geoLocale : intl.locale,
    currentRegionCode,
  )
  return useCallback(
    (
      startDate: SupportedDateType,
      endDate: SupportedDateType,
      includeYearWhenSameYear = true,
    ) =>
      intlHyphenDateRangeFormatter(
        startDate,
        endDate,
        locale,
        includeYearWhenSameYear,
      ),
    [locale],
  )
}

export function getDateFromMonthKey(monthKey: string) {
  const [year, month] = monthKey.split('-').map(Number)
  return new Date(year, month - 1)
}

export function getLastDayOfMonth(val: Date) {
  return new Date(val.getFullYear(), val.getMonth() + 1, 0).getDate()
}

export function diffInDays(date1: Date, date2: Date) {
  const t2 = date2.getTime()
  const t1 = date1.getTime()

  return Math.floor((t2 - t1) / (24 * 3600 * 1000))
}

const now = new Date()
/**
 * Returns the numeric value of the age in a specific date
 * @param {string} dobString - The date of birth in string
 * @param {Date} date - The date that we want to know what is the age, optional - will go from 'now' otherwise
 */
export function getAge(dobString: string, date: Date = now): number {
  const birthDate: Date = new Date(dobString)
  const age: number = date.getFullYear() - birthDate.getFullYear()
  const isDateBeforeBirthDate =
    date.getMonth() < birthDate.getMonth() ||
    (date.getMonth() === birthDate.getMonth() &&
      date.getDate() < birthDate.getDate())

  return age - (isDateBeforeBirthDate ? 1 : 0)
}

/**
 * Returns whether the booking is within the promotion date range
 * @param {string} bookingStartDate - The booking start date
 * @param {string} bookingEndDate - The booking end date
 * @param {string} promotionStartDate - The promotion start date
 * @param {string} promotionEndDate - The promotion end date
 * @returns {boolean} Whether the booking is within the promotion date range
 */
export function isBookingWithinDateRange(
  bookingStartDate: string,
  bookingEndDate: string,
  promotionStartDate: string,
  promotionEndDate: string,
) {
  const bookingStart = new Date(bookingStartDate)
  const bookingEnd = new Date(bookingEndDate)
  const promotionStart = new Date(promotionStartDate)
  const promotionEnd = new Date(promotionEndDate)

  return bookingStart >= promotionStart && bookingEnd <= promotionEnd
}

/**
 * @param {Date} startDate start of date range
 * @param {Date} endDate end of date range
 * @param {Date} date date to check
 * @returns {boolean} whether the date is between the start and end date
 */
export function isDateBetween(
  startDate: Date,
  endDate: Date,
  date = new Date(),
) {
  // take the time component out of the equation
  const timelessStart = new Date(startDate).setHours(0, 0, 0, 0)
  const timelessEnd = new Date(endDate).setHours(23, 0, 0, 0)
  const timelessCheck = new Date(date).setHours(12, 0, 0, 0)
  return timelessCheck >= timelessStart && timelessCheck <= timelessEnd
}

/**
 * @param {string} startDate start of date range
 * @param {string} endDate end of date range
 * @param {string} date date to check
 * @returns {boolean} whether the date is between or equal to the start and end date
 */
export function isDateBetweenOrEqual(
  startDate: string,
  endDate: string,
  date?: string,
) {
  const dateObj = date ? new Date(date) : new Date()
  const startDateObj = new Date(startDate)
  const endDateObj = new Date(endDate)
  return (
    isValidDate(dateObj) &&
    isValidDate(startDateObj) &&
    isValidDate(endDateObj) &&
    (isDateBetween(startDateObj, endDateObj, dateObj) ||
      isSameDay(dateObj, startDateObj) ||
      isSameDay(dateObj, endDateObj))
  )
}

const weekdayKeys = ['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat'] as const
type WeekdayKey = (typeof weekdayKeys)[number]

const weekdayNameCacheByLocale: {
  [locale: string]: {
    [key in WeekdayKey]: {
      long: string
      short: string
    }
  }
} = {}

export function getWeekdayNames(locale: string = 'en-AU') {
  if (!weekdayNameCacheByLocale[locale]) {
    const longFormatter = Intl.DateTimeFormat(locale, { weekday: 'long' })
    const shortFormatter = Intl.DateTimeFormat(locale, { weekday: 'short' })
    weekdayNameCacheByLocale[locale] = weekdayKeys.reduce((acc, key, i) => {
      // Intl currently doesn't have a way to just get the weekday names, so we have to use actual dates.
      // Oct 2023 started on a Sunday, and weekdayKeys starts from Sunday, so we can just use
      // dates 1-7 Oct to get the weekday names.
      const date = new Date(2023, 9, i + 1)
      acc[key] = {
        long: longFormatter.format(date),
        short: shortFormatter.format(date),
      }
      return acc
    }, {} as (typeof weekdayNameCacheByLocale)[string])
  }

  return weekdayNameCacheByLocale[locale]
}

export function daysInPast(date: Date): number {
  const currentDate = new Date()
  const timeDifference = currentDate.getTime() - date.getTime()
  const daysDifference = Math.floor(timeDifference / (1000 * 3600 * 24))
  return daysDifference
}

export function daysUntilDate(date: Date): number {
  const currentDate = new Date()
  const timeDifference = date.getTime() - currentDate.getTime()
  const daysDifference = Math.floor(timeDifference / (1000 * 3600 * 24))
  return daysDifference
}

export function formatDynamicDateRange(
  startDate?: Date | string | moment.Moment,
  endDate?: Date | string | moment.Moment,
  duration?: number,
) {
  if (!startDate || (!endDate && !duration)) return ''

  const intl = getCurrentIntl()
  const startMoment = moment(startDate)
  const endMoment = endDate
    ? moment(endDate)
    : moment(startDate).add(duration, 'days')

  if (!startMoment.isValid() || !endMoment.isValid()) {
    return ''
  }

  if (startMoment.isSame(endMoment, 'day')) {
    return intlDateFormatter(
      startMoment,
      INTL_DMY_CASUAL_SHORT_FORMAT,
      intl.locale,
    )
  }

  if (startMoment.isSame(endMoment, 'month')) {
    return intl.formatMessage(
      {
        id: 'K1Kr5d2j22khp6yRvt3U',
        defaultMessage: '{startDay} - {endDate}',
        description:
          'Date range where the start and end dates are in the same month and year',
      },
      {
        startDay: intlDateFormatter(startMoment, INTL_DAY_ONLY, intl.locale),
        endDate: intlDateFormatter(
          endMoment,
          INTL_DMY_CASUAL_SHORT_FORMAT,
          intl.locale,
        ),
      },
    )
  }

  return intl.formatMessage(
    {
      id: 'edhdMC77qJfnRCSwJcRs',
      defaultMessage: '{startDate} - {endDate}',
      description:
        'Date range with localized start and end dates shown in a shared date utility',
    },
    {
      startDate: intlDateFormatter(
        startMoment,
        startMoment.isSame(endMoment, 'year')
          ? INTL_DAY_MONTH_NAME_SHORT
          : INTL_DMY_CASUAL_SHORT_FORMAT,
        intl.locale,
      ),
      endDate: intlDateFormatter(
        endMoment,
        INTL_DMY_CASUAL_SHORT_FORMAT,
        intl.locale,
      ),
    },
  )
}

export function formatExperienceDateRange(
  item: App.Checkout.ExperienceItem,
  offer: App.ExperienceOffer,
): string | undefined {
  if (!item.date && !offer.hasTimeslots) return
  const intl = getCurrentIntl()
  const formattedDate = intlDateFormatter(
    item.date,
    INTL_DMY_CASUAL_FORMAT,
    intl.locale,
  )

  if (offer.hasTimeslots) {
    return intl.formatMessage(
      {
        id: 'hJXx0kom_8db08TznWs7',
        defaultMessage: '{date} · {time}',
        description:
          'Experience date and selected timeslot in cart and checkout contexts',
      },
      {
        date: formattedDate,
        time: item.time,
      },
    )
  }
  return formattedDate
}

export function formatDateDMYCasual(
  date: Date | string | moment.Moment,
): string {
  if (!date) return ''
  return intlDateFormatter(
    date,
    INTL_DMY_CASUAL_FORMAT,
    getCurrentIntl().locale,
  )
}

export const ISO_DATE_REGEX = /^(\d{4})-(\d{2})-(\d{2})/

/**
 * Adds the number of business days given to the date
 * A business day does not include weekends, so it will skip to the next business day
 * if it were to land on a weekend
 * @param date The date to add to
 * @param days The number of business days to add
 * @returns The date with additional business days added
 */
export function addBusinessDays(date: Date, days: number): Date {
  const newDate = new Date(date)
  // see how many weekends our days will spread over so we can add them to the next day
  const additionalWeekends = Math.floor(days / 5)
  newDate.setDate(newDate.getDate() + days + additionalWeekends * 2)

  // if we landed on a weekend day, shift it forward by a weekend again
  if (newDate.getDay() === 0 || newDate.getDay() === 6) {
    newDate.setDate(newDate.getDate() + 2)
  }

  return newDate
}

export function isFutureDateString(date: string) {
  return moment.utc(date, ISO_DATE_FORMAT).isAfter(moment.utc(), 'day')
}

export function isPresentOrFutureDateString(date: string) {
  return moment
    .utc(date, ISO_DATE_FORMAT, true)
    .isSameOrAfter(moment.utc(), 'day')
}

export type AccommodationCheckInPastOptions = {
  /**
   * Unit tests only: treat this instant as “now” for calendar-day comparison.
   * Do not pass from product code (Jest fake timers are incompatible with `timezone-mock`).
   */
  referenceTime?: Date
  /**
   * Property / hotel IANA timezone (`offer.property.timezone`) for “today” at the accommodation.
   * When set, calendar-day past matches the stay location (not storefront or server host TZ).
   */
  timeZone?: string
}

/**
 * Calendar date `YYYY-MM-DD` for an instant in an IANA timezone (for lexical compare).
 */
export function getCalendarDateStringInTimeZone(
  date: Date,
  timeZone: string,
): string {
  try {
    const dtf = new Intl.DateTimeFormat('en-CA', {
      timeZone,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    })
    const parts = dtf.formatToParts(date)
    const y = parts.find((p) => p.type === 'year')?.value
    const m = parts.find((p) => p.type === 'month')?.value
    const d = parts.find((p) => p.type === 'day')?.value
    if (!y || !m || !d) {
      return moment(date).format(ISO_DATE_FORMAT)
    }
    const pad = (x: string) => x.padStart(2, '0')
    return `${y}-${pad(m)}-${pad(d)}`
  } catch {
    return moment(date).format(ISO_DATE_FORMAT)
  }
}

/**
 * True when the ISO check-in date is strictly before “today” in the chosen calendar sense.
 *
 * - With `timeZone`: compares using that IANA zone’s calendar day for `now` (property timezone for SSR/client parity).
 * - Without `timeZone` (e.g. offer not loaded): uses the runtime’s local calendar day via moment.
 */
export function isAccommodationCheckInDatePast(
  isoDate: string | undefined,
  options?: AccommodationCheckInPastOptions,
): boolean {
  if (!isoDate) {
    return false
  }
  const checkIn = moment(isoDate, ISO_DATE_FORMAT, true)
  if (!checkIn.isValid()) {
    return false
  }
  const now = options?.referenceTime ?? new Date()
  if (options?.timeZone) {
    const todayInZone = getCalendarDateStringInTimeZone(now, options.timeZone)
    return isoDate < todayInZone
  }
  return checkIn.isBefore(moment(now), 'day')
}

export interface RecurringDateRange {
  startMonth: number // 1-12 (human readable)
  startDay: number
  endMonth: number // 1-12 (human readable)
  endDay: number
}

/**
 * Checks if a date period overlaps with a recurring annual date range.
 * Automatically handles year boundary when endMonth < startMonth (e.g., Dec 15 - Jan 15).
 * Uses 1-indexed months for readability (1 = January, 12 = December).
 *
 * Note: Only checks one period window based on start date. Very long periods (300+ days)
 * that start after one range and extend into the next year's range won't be detected.
 * This is acceptable for typical use cases (1-30 days).
 *
 * @param startDate - Start date string
 * @param endDate - End date string
 * @param range - The recurring annual date range to check against
 * @param dateFormat - The format of the date strings (e.g., PICKER_DATE_FORMAT)
 */
export function overlapsRecurringDateRange(
  startDate: string,
  endDate: string,
  range: RecurringDateRange,
  dateFormat: string,
): boolean {
  const start = moment(startDate, dateFormat)
  const end = moment(endDate, dateFormat)
  const startYear = start.year()
  const startMonth = start.month() + 1 // Convert to 1-indexed

  // Convert to 0-indexed for moment
  const rangeStartMonth = range.startMonth - 1
  const rangeEndMonth = range.endMonth - 1

  const wrapsYear = range.endMonth < range.startMonth
  const rangeStartYear =
    wrapsYear && startMonth <= range.endMonth ? startYear - 1 : startYear
  const rangeEndYear = rangeStartYear + (wrapsYear ? 1 : 0)

  const rangeStart = moment({
    year: rangeStartYear,
    month: rangeStartMonth,
    date: range.startDay,
  })
  const rangeEnd = moment({
    year: rangeEndYear,
    month: rangeEndMonth,
    date: range.endDay,
  })

  return start.isSameOrBefore(rangeEnd) && rangeStart.isSameOrBefore(end)
}
